import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

// URL de l'API - utilise l'origine actuelle (même serveur que le React)
const API_URL = window.location.origin;

export default function NewCallModal({ isOpen, onClose, darkMode, authFetch }) {
  const [client, setClient] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Focus sur le champ texte à l'ouverture
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 100);
    }
    // Réinitialiser le formulaire à l'ouverture
    if (isOpen) {
      setClient('');
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!client.trim()) {
      setError('Veuillez entrer un nom de client');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Enregistrer le client en attente pour la prochaine fiche
      const response = await authFetch('/api/pending-client', {
        method: 'POST',
        body: JSON.stringify({ client: client.trim() })
      });

      const data = await response.json();

      if (data.success || response.ok) {
        setClient('');
        onClose();
      } else {
        setError(data.error || 'Erreur lors de l\'enregistrement');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  const bgMain = darkMode ? 'bg-slate-800' : 'bg-white';
  const bgOverlay = 'bg-black/50';
  const textMain = darkMode ? 'text-white' : 'text-gray-900';
  const textMuted = darkMode ? 'text-slate-400' : 'text-gray-500';
  const border = darkMode ? 'border-slate-600' : 'border-gray-300';
  const inputBg = darkMode ? 'bg-slate-700' : 'bg-gray-50';

  return (
    <div className={`fixed inset-0 ${bgOverlay} flex items-center justify-center z-50`}>
      <div className={`${bgMain} rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className={`text-lg font-semibold ${textMain}`}>📞 Nouvel appel</h2>
          <button
            onClick={onClose}
            className={`p-1 rounded-lg hover:bg-slate-700 transition ${textMuted}`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4">
          <div className="mb-4">
            <label className={`block text-sm font-medium mb-2 ${textMain}`}>
              Client
            </label>
            <input
              ref={inputRef}
              type="text"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ex: 3050"
              className={`w-full px-4 py-3 rounded-lg ${inputBg} ${textMain} border ${border} focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-lg`}
              disabled={isSubmitting}
              autoComplete="off"
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-3 rounded-lg border ${border} ${textMain} font-medium hover:bg-slate-700 transition`}
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition disabled:bg-gray-600"
              disabled={isSubmitting}
            >
              {isSubmitting ? '⏳...' : '✅ OK'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
